import * as React from "react";
import { GlobalSearchProvider, useGlobalSearch } from "./GlobalSearch/context";
import { GlobalSearchModal } from "./GlobalSearch/Modal";
import { CommandShortcuts } from "./CommandShortcuts";
import { LayoutOptionsProvider } from "./layoutOptions";
import { NavigationProvider } from "../navigation/NavigationProvider";
import { UnifiedSidebar } from "../navigation/UnifiedSidebar";
import { Breadcrumbs } from "../navigation/Breadcrumbs";
import {
  SidebarRail,
  SidebarInset,
  SidebarTrigger,
} from "../../../components/ui/sidebar";
import { PersistentSidebarProvider } from "./PersistentSidebarProvider";
import { Separator } from "../../../components/ui/separator";
import { Button } from "../../../components/ui/button";
import { Search, Bell } from "lucide-react";
import { useAuth } from "wasp/client/auth";
import { useNavigate } from "react-router-dom";

const FrameContext = React.createContext<boolean>(false);

function AppFrameInner({ children }: { children: React.ReactNode }) {
  const { open, setOpen } = useGlobalSearch();
  const { data: user } = useAuth();
  const navigate = useNavigate();
  return (
    <>
      <PersistentSidebarProvider>
        <UnifiedSidebar />
        {/* Keep Sidebar immediately before SidebarInset for peer styling */}
        <SidebarInset className="min-w-0 flex-1 overflow-y-auto">
          {/* shadcn Sidebar 08-style header */}
          <header className="sticky top-0 z-20 border-b bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="relative mx-auto flex h-16 w-full max-w-[min(96rem,calc(100%-2rem))] items-center gap-2 px-4 sm:px-6 md:px-8">
              <SidebarTrigger className="-ml-0.5 inline-flex h-9 w-9 shrink-0 items-center justify-center" />
              <Separator orientation="vertical" className="mx-2 h-6" />
              <div className="hidden min-w-0 flex-1 items-center sm:flex">
                <Breadcrumbs className="truncate" />
              </div>
              {/* Mobile-only centered brand */}
              <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center sm:hidden">
                <span className="text-sm font-semibold">Nura</span>
              </div>
              {/* Right actions */}
              <div className="ml-auto flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Open search"
                  onClick={() => setOpen(true)}
                >
                  <Search className="size-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label="Notifications"
                  onClick={() => navigate('/settings?section=notifications')}
                >
                  <Bell className="size-5" />
                </Button>
              </div>
            </div>
          </header>
          {/* Content viewport aligned with header padding/width */}
          <main className="min-w-0 flex-1">
            <div className="mx-auto w-full max-w-[min(96rem,calc(100%-2rem))] px-4 py-6 sm:px-6 md:px-8">
              {children}
            </div>
          </main>
        </SidebarInset>
      </PersistentSidebarProvider>
      <GlobalSearchModal
        isOpen={open}
        onClose={() => setOpen(false)}
        onSelect={(result) => {
          console.log("Selected:", result);
          setOpen(false);
        }}
      />
    </>
  );
}

export function AppFrame({ children }: { children: React.ReactNode }) {
  const isNested = React.useContext(FrameContext);
  if (isNested) {
    // Nested AppFrame: render content only to avoid double sidebars/overlays
    return <>{children}</>;
  }
  return (
    <FrameContext.Provider value={true}>
      <GlobalSearchProvider>
        <LayoutOptionsProvider>
          <NavigationProvider>
            <CommandShortcuts />
            <AppFrameInner>{children}</AppFrameInner>
          </NavigationProvider>
        </LayoutOptionsProvider>
      </GlobalSearchProvider>
    </FrameContext.Provider>
  );
}

// Re-export for legacy callers that import from AppFrame (compat)
export { useLayoutOptions } from "./layoutOptions";
